import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import { formatAwbOnInput, parseAwb, validateAwb } from '../../utils/awb';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Print as PrintIcon,
  QrCode2 as QrCodeIcon,
  CheckCircle as SuccessIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

const Reception = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Tipo de recepción: 'walk_in' (con AWB), 'with_reservation' (con reserva), 'no_awb' (sin AWB)
  const [receptionType, setReceptionType] = useState('walk_in');

  // Datos principales
  const [awb, setAwb] = useState('');
  const [whNumber, setWhNumber] = useState('');
  const [hasDocumentation, setHasDocumentation] = useState(false);
  const [destination, setDestination] = useState('');
  const [clientReference, setClientReference] = useState(''); // Referencia del cliente (para items sin AWB)
  
  // Modo de entrada
  const [enterByTotals, setEnterByTotals] = useState(false);
  
  // Totales (cuando se ingresa por totales)
  const [totalPieces, setTotalPieces] = useState('');
  const [totalWeight, setTotalWeight] = useState('');
  const [totalVolume, setTotalVolume] = useState('0');

  // Cajas individuales
  const [boxes, setBoxes] = useState([
    { id: 1, pieces: 1, weight: '', length: '', width: '', height: '' }
  ]);

  // Datos para selects
  const [estaciones, setEstaciones] = useState([]);
  const [awbOptions, setAwbOptions] = useState([]);
  const [searchingAwb, setSearchingAwb] = useState(false);

  // Constante para cálculo de volumen
  const DIMENSIONAL_FACTOR = 166; // Factor para convertir in³ a lb

  useEffect(() => {
    fetchData();
  }, []);

  // Cargar datos si está en modo edición
  useEffect(() => {
    if (isEditMode && id) {
      loadReceptionData();
    }
  }, [id, isEditMode]);

  const loadReceptionData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`${endpoints.warehouse.reception}?id=${id}`);
      const data = res.data.data?.reception || res.data.data;
      
      if (data) {
        setReceptionType(data.reception_type || 'walk_in');
        // AWB es un campo separado de la referencia
        setAwb(data.awb ? formatAwbOnInput(data.awb) : '');
        // La referencia siempre es WH-XXXX (extraer el número)
        const refNumber = data.reference?.replace('WH-', '') || data.id?.toString() || '';
        setWhNumber(refNumber);
        setHasDocumentation(data.has_documentation || false);
        setDestination(data.destination || '');
        setTotalPieces(data.pieces_received?.toString() || '');
        setTotalWeight(data.weight_received?.toString() || '');
        setTotalVolume(data.volume?.toString() || '0');
        setClientReference(data.notes || ''); // Cargar notas/referencia del cliente
        setEnterByTotals(true); // Por defecto mostrar totales si viene de edición
      }
    } catch (error) {
      console.error('Error cargando recepción:', error);
      setError('Error al cargar los datos de la recepción');
    } finally {
      setLoading(false);
    }
  };

  // Limpiar AWB cuando se cambia a "sin AWB"
  useEffect(() => {
    if (receptionType === 'no_awb') {
      setAwb('');
    }
  }, [receptionType]);

  // Generar URL del QR - siempre usa la referencia WH (auto-generada)
  const whDetailUrl = useMemo(() => {
    if (!whNumber) return '';
    // Usar el mismo dominio donde se está viendo la web
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    return `${baseUrl}/warehouse/detail/WH-${whNumber}`;
  }, [whNumber]);

  const qrCodeUrl = useMemo(() => {
    if (!whDetailUrl) return '';
    return `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(whDetailUrl)}`;
  }, [whDetailUrl]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [estacionesRes, seqRes] = await Promise.all([
        api.get(endpoints.estaciones).catch(() => ({ data: { data: { data: [] } } })),
        api.get(endpoints.warehouse.sequence).catch(() => ({ data: { data: { next_number: 1 } } })),
      ]);
      
      const estacionesData = estacionesRes.data.data?.data || estacionesRes.data.data || [];
      setEstaciones(estacionesData);
      
      // Generar número de WH (siempre auto-generado)
      const seqData = seqRes.data.data;
      const nextNum = seqData?.number || seqData?.next_number || seqData?.sequence || 1;
      setWhNumber(nextNum.toString());
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Búsqueda de AWBs
  const handleAwbSearch = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      setAwbOptions([]);
      return;
    }

    try {
      setSearchingAwb(true);
      const res = await api.get(`${endpoints.warehouse.search}?q=${encodeURIComponent(inputValue)}&limit=10`);
      setAwbOptions(res.data.data?.results || []);
    } catch (error) {
      console.error('Error buscando AWBs:', error);
      setAwbOptions([]);
    } finally {
      setSearchingAwb(false);
    }
  };

  // Seleccionar AWB
  const handleAwbSelect = (selectedOption) => {
    if (!selectedOption) {
      setAwb('');
      return;
    }
    const awbValue = selectedOption.awb || selectedOption.reference || selectedOption;
    setAwb(typeof awbValue === 'string' ? awbValue : '');
    if (selectedOption.destination) {
      setDestination(selectedOption.destination);
    }
  };

  // Agregar caja
  const addBox = () => {
    const newId = Math.max(...boxes.map(b => b.id)) + 1;
    setBoxes([...boxes, { id: newId, pieces: 1, weight: '', length: '', width: '', height: '' }]);
  };

  // Eliminar caja
  const removeBox = (id) => {
    if (boxes.length === 1) return;
    setBoxes(boxes.filter(b => b.id !== id));
  };

  // Actualizar caja
  const updateBox = (id, field, value) => {
    setBoxes(boxes.map(b => 
      b.id === id ? { ...b, [field]: value } : b
    ));
  };

  // Calcular volumen de una caja (in³ a lb)
  const calculateBoxVolume = (box) => {
    const l = parseFloat(box.length) || 0;
    const w = parseFloat(box.width) || 0;
    const h = parseFloat(box.height) || 0;
    if (l && w && h) {
      return (l * w * h) / DIMENSIONAL_FACTOR;
    }
    return 0;
  };

  // Totales calculados
  const calculatedTotals = useMemo(() => {
    if (enterByTotals) {
      const weight = parseFloat(totalWeight) || 0;
      const volume = parseFloat(totalVolume) || 0;
      const chargeable = Math.max(weight, volume);
      return {
        pieces: parseInt(totalPieces) || 0,
        weight: weight.toFixed(2),
        volume: volume.toFixed(2),
        chargeable: chargeable.toFixed(2),
      };
    }

    let pieces = 0;
    let weight = 0;
    let volume = 0;

    boxes.forEach(box => {
      pieces += parseInt(box.pieces) || 0;
      weight += parseFloat(box.weight) || 0;
      volume += calculateBoxVolume(box);
    });

    const chargeable = Math.max(weight, volume);

    return {
      pieces,
      weight: weight.toFixed(2),
      volume: volume.toFixed(2),
      chargeable: chargeable.toFixed(2),
    };
  }, [boxes, enterByTotals, totalPieces, totalWeight, totalVolume]);

  // Imprimir etiquetas
  const handlePrintLabels = (boxId = null) => {
    const printData = {
      awb: awb || `WH-${whNumber}`,
      whNumber,
      destination,
      boxes: boxId ? boxes.filter(b => b.id === boxId) : boxes,
    };
    
    // Abrir ventana de impresión
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Etiquetas - ${printData.awb}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .label {
              border: 2px solid #000;
              padding: 15px;
              margin-bottom: 20px;
              page-break-inside: avoid;
              width: 300px;
            }
            .label-header { font-size: 18px; font-weight: bold; text-align: center; border-bottom: 1px solid #000; padding-bottom: 10px; margin-bottom: 10px; }
            .label-row { display: flex; justify-content: space-between; margin: 5px 0; }
            .label-qr { text-align: center; margin: 10px 0; }
            .label-qr img { width: 80px; height: 80px; }
            @media print { .label { page-break-after: always; } }
          </style>
        </head>
        <body>
          ${printData.boxes.map((box, idx) => `
            <div class="label">
              <div class="label-header">WH #${printData.whNumber}</div>
              <div class="label-row"><span>AWB:</span><strong>${printData.awb}</strong></div>
              <div class="label-row"><span>Destino:</span><strong>${printData.destination || '-'}</strong></div>
              <div class="label-row"><span>Caja:</span><strong>${idx + 1} de ${printData.boxes.length}</strong></div>
              <div class="label-row"><span>Piezas:</span><strong>${box.pieces}</strong></div>
              <div class="label-row"><span>Peso:</span><strong>${box.weight || '-'} kg</strong></div>
              ${box.length ? `<div class="label-row"><span>Dimensiones:</span><strong>${box.length}" x ${box.width}" x ${box.height}"</strong></div>` : ''}
              <div class="label-qr">
                <img src="${qrCodeUrl}" alt="QR"/>
              </div>
            </div>
          `).join('')}
          <script>window.onload = function() { window.print(); }</script>
        </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  // Guardar
  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    // Validaciones
    if (receptionType === 'walk_in') {
      if (!awb) {
        setError('Ingrese el AWB');
        return;
      }
      if (!validateAwb(awb)) {
        setError('AWB inválido. Debe tener 11 dígitos (formato: 123-45678900)');
        return;
      }
    }
    if (!destination) {
      setError('Seleccione un destino');
      return;
    }
    if (calculatedTotals.pieces === 0) {
      setError('Debe ingresar al menos una pieza');
      return;
    }
    if (parseFloat(calculatedTotals.weight) === 0) {
      setError('Debe ingresar el peso');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        id: isEditMode ? parseInt(id) : undefined,
        reception_type: receptionType,
        awb: receptionType === 'walk_in' && awb ? parseAwb(awb) : null,
        destination,
        pieces_received: calculatedTotals.pieces,
        weight_received: parseFloat(calculatedTotals.weight),
        volume: parseFloat(calculatedTotals.volume) || 0,
        chargeable_weight: parseFloat(calculatedTotals.chargeable),
        has_documentation: hasDocumentation,
        notes: clientReference || null, // Referencia del cliente
        boxes: enterByTotals ? null : boxes.map((b, idx) => ({
          box_number: idx + 1,
          pieces: parseInt(b.pieces) || 1,
          weight: parseFloat(b.weight) || 0,
          length: parseFloat(b.length) || null,
          width: parseFloat(b.width) || null,
          height: parseFloat(b.height) || null,
        })),
      };

      let res;
      if (isEditMode) {
        res = await api.put(endpoints.warehouse.reception, payload);
        setSuccess('Recepción actualizada exitosamente');
      } else {
        res = await api.post(endpoints.warehouse.list, payload);
        const data = res.data.data;
        setSuccess(`Recepción registrada: ${data.reference || `WH-${whNumber}`}`);
      }

      setTimeout(() => {
        navigate('/warehouse/inventory');
      }, 2000);

    } catch (error) {
      setError(error.response?.data?.message || `Error al ${isEditMode ? 'actualizar' : 'registrar'} recepción`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      {/* Título */}
      <Typography variant="h5" fontWeight="bold" textAlign="center" sx={{ mb: 3, color: 'primary.main' }}>
        {isEditMode ? 'Editar WH del Depósito' : 'Agregar Nuevo WH al Depósito'}
      </Typography>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<SuccessIcon />}>
          {success}
        </Alert>
      )}

      {/* Tipo de Recepción */}
      <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Tipo de Recepción:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={receptionType === 'walk_in' ? 'contained' : 'outlined'}
              onClick={() => setReceptionType('walk_in')}
              sx={{ flex: 1 }}
            >
              Con AWB / Guía
            </Button>
            <Button
              variant={receptionType === 'no_awb' ? 'contained' : 'outlined'}
              color="warning"
              onClick={() => setReceptionType('no_awb')}
              sx={{ flex: 1 }}
            >
              Sin AWB (Terrestre)
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Header Info Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 2, 
        mb: 3,
        '& > *': {
          flex: '1 1 calc(25% - 16px)',
          minWidth: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(25% - 16px)' }
        }
      }}>
        {/* AWB */}
        <Paper sx={{ 
          p: 2, 
          textAlign: 'center', 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: 140,
          border: '1px solid', 
          borderColor: 'divider' 
        }}>
            <Typography variant="caption" color="text.secondary" display="block">
              AWB:
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
              {receptionType === 'walk_in' ? (
                <Autocomplete
                  freeSolo
                  options={awbOptions}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.display || option.awb || option.reference || '';
                  }}
                  inputValue={awb}
                  onInputChange={(_, newInputValue) => {
                    setAwb(formatAwbOnInput(newInputValue));
                    handleAwbSearch(newInputValue);
                  }}
                  onChange={(_, newValue) => handleAwbSelect(newValue)}
                  loading={searchingAwb}
                  filterOptions={(x) => x}
                  sx={{ width: '100%' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Ingrese AWB"
                      InputProps={{
                        ...params.InputProps,
                        sx: { fontSize: '1.1rem', fontWeight: 'bold', textAlign: 'center' },
                        endAdornment: (
                          <>
                            {searchingAwb ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      inputProps={{
                        ...params.inputProps,
                        style: { textAlign: 'center' }
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                      <li key={key} {...rest}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <Typography fontWeight="bold">{option.display || option.awb}</Typography>
                          <Chip label={option.destination} size="small" variant="outlined" />
                        </Box>
                      </li>
                    );
                  }}
                />
              ) : (
                <Typography variant="h6" fontWeight="bold" color="warning.main">
                  Sin AWB
                </Typography>
              )}
            </Box>
          </Paper>

        {/* WH # - Siempre auto-generado */}
        <Paper sx={{ 
          p: 2, 
          textAlign: 'center', 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: 140,
          border: '1px solid', 
          borderColor: 'divider' 
        }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Referencia WH:
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                WH-{whNumber}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                (Auto-generado)
              </Typography>
            </Box>
          </Paper>

        {/* Documentación */}
        <Paper sx={{ 
          p: 2, 
          textAlign: 'center', 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: 140,
          border: '1px solid', 
          borderColor: 'divider' 
        }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Documentación:
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasDocumentation}
                    onChange={(e) => setHasDocumentation(e.target.checked)}
                  />
                }
                label={hasDocumentation ? 'Sí' : 'No'}
              />
            </Box>
          </Paper>

        {/* QR Code */}
        <Paper sx={{ 
          p: 2, 
          textAlign: 'center', 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: 140,
          border: '1px solid', 
          borderColor: 'divider' 
        }}>
            <Typography variant="caption" color="text.secondary" display="block">
              QR:
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
              {qrCodeUrl ? (
                <img src={qrCodeUrl} alt="QR Code" style={{ width: 80, height: 80 }} />
              ) : (
                <QrCodeIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
              )}
            </Box>
          </Paper>
      </Box>

      {/* Destino */}
      <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box>
          <FormControl fullWidth size="small" required>
              <InputLabel>Destino</InputLabel>
              <Select
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                label="Destino"
              >
                <MenuItem value="" disabled>Seleccionar destino</MenuItem>
                {estaciones.map(e => (
                  <MenuItem key={e.id} value={e.code}>
                    <strong>{e.code}</strong>&nbsp;- {e.Nombre || e.Pais || ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
        </Box>
      </Paper>

      {/* Referencia del Cliente (solo para items sin AWB) */}
      {receptionType === 'no_awb' && (
        <Paper sx={{ p: 2, mb: 3, border: '2px solid', borderColor: 'warning.main', bgcolor: 'warning.50' }}>
          <Box>
            <Typography variant="subtitle2" color="warning.dark" gutterBottom fontWeight="bold">
              📋 Referencia del Cliente (para identificar la carga)
            </Typography>
            <TextField
              fullWidth
              value={clientReference}
              onChange={(e) => setClientReference(e.target.value)}
              placeholder="Ej: Pedido #12345, Factura ABC-001, Orden de compra, etc."
              helperText="Ingrese una referencia que ayude a identificar esta carga sin AWB"
              variant="outlined"
              size="small"
              sx={{ bgcolor: 'white' }}
            />
          </Box>
        </Paper>
      )}

      {/* Toggle Modo Totales */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={enterByTotals}
              onChange={(e) => setEnterByTotals(e.target.checked)}
            />
          }
          label="Ingresar por totales (sin medidas)"
        />
      </Box>

      {/* Modo por Totales */}
      {enterByTotals ? (
        <Paper 
          sx={{ 
            p: 3, 
            mb: 3, 
            bgcolor: '#FFFDE7', 
            border: '2px solid', 
            borderColor: 'warning.main', 
            borderRadius: 2 
          }}
        >
          <Typography variant="h6" fontWeight="bold" textAlign="center" sx={{ mb: 3 }}>
            Ingreso por Totales
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 0 }}>
            <Box sx={{ flex: 1, textAlign: 'center', mb: 1 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>Total Piezas</Typography>
              <TextField
                type="number"
                value={totalPieces}
                onChange={(e) => setTotalPieces(e.target.value)}
                fullWidth
                inputProps={{ min: 1 }}
                sx={{ 
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: { xs: 1, sm: 0 },
                    borderTopLeftRadius: { sm: 4 },
                    borderBottomLeftRadius: { sm: 4 },
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center', mb: 1 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>Peso Total (kg)</Typography>
              <TextField
                type="number"
                value={totalWeight}
                onChange={(e) => setTotalWeight(e.target.value)}
                fullWidth
                inputProps={{ min: 0.1, step: 0.1 }}
                sx={{ 
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: { xs: 1, sm: 0 },
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center', mb: 1 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>Volumen Total (lb)</Typography>
              <TextField
                type="number"
                value={totalVolume}
                onChange={(e) => setTotalVolume(e.target.value)}
                fullWidth
                inputProps={{ min: 0, step: 0.1 }}
                placeholder="0"
                helperText="(opcional, si no sabes pon 0)"
                sx={{ 
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: { xs: 1, sm: 0 },
                    borderTopRightRadius: { sm: 4 },
                    borderBottomRightRadius: { sm: 4 },
                  }
                }}
              />
            </Box>
          </Box>
        </Paper>
      ) : (
        <>
          {/* Cajas Individuales */}
          {boxes.map((box, index) => (
            <Paper
              key={box.id}
              sx={{
                mb: 2,
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {/* Header de Caja */}
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  px: 2,
                  py: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography fontWeight="bold">
                  Caja #{index + 1}
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => removeBox(box.id)}
                  disabled={boxes.length === 1}
                  sx={{ minWidth: 'auto', px: 2 }}
                >
                  Eliminar
                </Button>
              </Box>

              {/* Campos de Caja */}
              <Box sx={{ p: 2 }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 2, 
                  alignItems: 'flex-end',
                  '& > *': {
                    flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 11px)', md: '1 1 calc(16.666% - 11px)' },
                    minWidth: { xs: 'calc(50% - 8px)', sm: 'calc(33.333% - 11px)', md: 'calc(16.666% - 11px)' }
                  }
                }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Piezas
                    </Typography>
                    <TextField
                      type="number"
                      value={box.pieces}
                      onChange={(e) => updateBox(box.id, 'pieces', e.target.value)}
                      fullWidth
                      size="small"
                      inputProps={{ min: 1 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Peso (kg)
                    </Typography>
                    <TextField
                      type="number"
                      value={box.weight}
                      onChange={(e) => updateBox(box.id, 'weight', e.target.value)}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0.1, step: 0.1 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Largo (in)
                    </Typography>
                    <TextField
                      type="number"
                      value={box.length}
                      onChange={(e) => updateBox(box.id, 'length', e.target.value)}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0, step: 0.5 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Ancho (in)
                    </Typography>
                    <TextField
                      type="number"
                      value={box.width}
                      onChange={(e) => updateBox(box.id, 'width', e.target.value)}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0, step: 0.5 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Alto (in)
                    </Typography>
                    <TextField
                      type="number"
                      value={box.height}
                      onChange={(e) => updateBox(box.id, 'height', e.target.value)}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0, step: 0.5 }}
                    />
                  </Box>
                  <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(16.666% - 11px)' } }}>
                    <Button
                      variant="contained"
                      color="success"
                      fullWidth
                      startIcon={<PrintIcon />}
                      onClick={() => handlePrintLabels(box.id)}
                      sx={{ height: 40 }}
                    >
                      Imprimir Etiquetas
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          ))}

          {/* Botón Agregar Caja */}
          <Button
            variant="contained"
            color="error"
            startIcon={<AddIcon />}
            onClick={addBox}
            sx={{ mb: 3 }}
          >
            + Agregar Otra Caja
          </Button>
        </>
      )}

      {/* Totales */}
      <Paper
        sx={{
          mb: 3,
          border: '2px solid',
          borderColor: 'grey.700',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            bgcolor: 'grey.800',
            color: 'white',
            px: 3,
            py: 1.5,
          }}
        >
          <Typography fontWeight="bold">
            Totales
          </Typography>
        </Box>
        <Box sx={{ bgcolor: 'grey.700', color: 'white', px: 3, py: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2,
            '& > *': {
              flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(25% - 12px)' }
            }
          }}>
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Piezas:</Typography>
              <Typography variant="h6" fontWeight="bold">{calculatedTotals.pieces}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Peso:</Typography>
              <Typography variant="h6" fontWeight="bold">{calculatedTotals.weight} kg</Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Volumen:</Typography>
              <Typography variant="h6" fontWeight="bold">{calculatedTotals.volume} lb</Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Chargeable:</Typography>
              <Typography variant="h6" fontWeight="bold">{calculatedTotals.chargeable} kg</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Botones de Acción */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button
          variant="contained"
          color="success"
          size="large"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          sx={{ px: 4, py: 1.5 }}
        >
          {saving ? 'Guardando...' : 'Guardar y Finalizar'}
        </Button>
        <Button
          variant="contained"
          color="inherit"
          size="large"
          component={Link}
          to="/warehouse"
          startIcon={<CancelIcon />}
          sx={{ px: 4, py: 1.5, bgcolor: 'grey.500', '&:hover': { bgcolor: 'grey.600' } }}
        >
          Cancelar
        </Button>
      </Box>
    </Box>
  );
};

export default Reception;
